import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import { useCIQLazyQuery } from "hooks/ciq-gql-hooks";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  QUERY_GET_SCHEDULE_VERSIONS,
  QUERY_WORKFLOW_DEFAULT
} from "services/graphQL/queries";

function useGetScheduleListData() {
  const { eventLogs }: TProjectContext = useContext(ProjectContext);

  const [scheduleListResult, setScheduleListResult] = useState<{
    data: any;
    loading: boolean;
    error: any;
  }>({
    data: null,
    loading: false,
    error: null
  });
  const [getScheduleListData] = useCIQLazyQuery(QUERY_GET_SCHEDULE_VERSIONS);

  const fetchScheduleListData = useCallback(async () => {
    try {
      const res = await getScheduleListData();
      setScheduleListResult({
        data: res?.data?.data,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error("Failed to fetch schedule list data:", error);
      setScheduleListResult({
        data: null,
        loading: false,
        error
      });
    }
  }, [getScheduleListData]);

  useEffect(() => {
    fetchScheduleListData();
  }, [fetchScheduleListData]);

  useEffect(() => {
    const eventMatched = eventLogs?.some(
      (event: any) => event.data_source === "schedule_import_details"
    );
    if (eventMatched) {
      fetchScheduleListData();
    }
  }, [eventLogs, fetchScheduleListData]);

  return scheduleListResult;
}

function useGetWorkflowDefaults({ featureId }: { featureId: FeatureTypes }) {
  const { eventLogs }: TProjectContext = useContext(ProjectContext);

  const [workflowDefaultsResult, setWorkflowDefaultsResult] = useState<{
    data: any;
    loading: boolean;
    error: any;
  }>({
    data: null,
    loading: false,
    error: null
  });
  const [getWorkflowDefaults] = useCIQLazyQuery(QUERY_WORKFLOW_DEFAULT);

  const fetchWorkflowDefaults = useCallback(async () => {
    try {
      const res = await getWorkflowDefaults({
        where: { feature_id: { _eq: featureId } }
      });
      setWorkflowDefaultsResult({
        data: res?.data?.data,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error("Failed to fetch workflow defaults:", error);
      setWorkflowDefaultsResult({
        data: null,
        loading: false,
        error
      });
    }
  }, [getWorkflowDefaults, featureId]);

  useEffect(() => {
    fetchWorkflowDefaults();
  }, [fetchWorkflowDefaults]);

  useEffect(() => {
    const eventMatched = eventLogs?.some(
      (event: any) => event.data_source === "project_feature_configurations"
    );
    if (eventMatched) {
      fetchWorkflowDefaults();
    }
  }, [eventLogs, fetchWorkflowDefaults]);

  return workflowDefaultsResult;
}

export { useGetScheduleListData, useGetWorkflowDefaults };
